import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { useListOrgsQuery } from '@proaxiv/server-api'
import type { ListOrganizationsResponse, OrganizationSummary } from '@proaxiv/server-api'
import { useAnchoredMenuPosition } from '../hooks/useAnchoredMenuPosition'

const BuildingIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18M3 9h18M3 15h18" />
  </svg>
)

const PlusIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
    className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M2 4l4 4 4-4" />
  </svg>
)

/**
 * Displays the user's organization context in the header.
 *
 * - 0 orgs: renders nothing (no org list to show yet; "New Organization" stays in the
 *   sidebar's main nav in that case — see `Sidebar.tsx`).
 * - 1+ orgs: shows a dropdown to switch between orgs, with "New Organization" pinned at
 *   the bottom of the list (see issue #200 — it moved out of the sidebar nav once the user
 *   already has at least one organization).
 *
 * The selected org is stored in `ActiveOrgContext` (localStorage-backed) so that
 * switching orgs updates the home page immediately and persists across reloads.
 */
export function OrgSwitcher() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: rawData } = useListOrgsQuery({})
  const orgs: OrganizationSummary[] =
    (rawData as ListOrganizationsResponse | undefined)?.organizations ?? []
  const { activeOrgId, setActiveOrgId } = useActiveOrg()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  // Renders the dropdown via a portal anchored with `position: fixed`, computed from the
  // trigger's real on-screen coordinates — the header is `position: fixed` and packs several
  // flex items into a tight row, so a plain CSS `absolute` menu could end up misaligned or
  // clipped on narrow (phone) viewports. Anchoring off real coordinates keeps it pinned under
  // the button regardless of how the surrounding header layout squeezes on mobile.
  const menuPosition = useAnchoredMenuPosition(containerRef, open, 'right')

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      // The menu is rendered via a portal into `document.body`, so it lives outside
      // `containerRef` and must be checked separately.
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const activeOrg = orgs.find((o) => o.orgId === activeOrgId)

  if (orgs.length === 0) return null

  // 1+ orgs — always a dropdown so "New Organization" has somewhere to live at the bottom
  // of the list, even when there's only one org to switch between.
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="hover:bg-pax-light-purple text-pax-black flex max-w-[40vw] cursor-pointer items-center gap-1.5 rounded-xl px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none sm:max-w-none md:px-3"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <BuildingIcon />
        <span className="truncate">{activeOrg?.name}</span>
        <ChevronDown open={open} />
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translateX(-100%)',
            }}
            className="border-pax-purple/30 z-50 w-52 overflow-hidden rounded-xl border bg-white p-1 shadow-lg"
          >
            {orgs.map((org) => (
              <li key={org.orgId} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={org.orgId === activeOrgId}
                  onClick={() => {
                    setActiveOrgId(org.orgId)
                    setOpen(false)
                  }}
                  className={[
                    'w-full cursor-pointer rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    // `pax-light-purple` is the page-background token, which the TMS/JPPA
                    // brands remap to pure white (see index.css) — using it for hover/selected
                    // state made rows invisible against the white dropdown under those brands.
                    // `pax-light-blue` stays a visible tint across every brand.
                    org.orgId === activeOrgId
                      ? 'bg-pax-light-blue text-pax-blue font-medium'
                      : 'text-pax-black hover:bg-pax-light-blue',
                  ].join(' ')}
                >
                  {org.name}
                </button>
              </li>
            ))}
            {/* "New Organization" lives at the end of the org list once the user already has
                at least one — see issue #200. */}
            <li role="none" className="border-pax-purple/20 mt-1 border-t pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  navigate('/organizations/new')
                }}
                className="text-pax-green hover:bg-pax-green/10 flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors"
              >
                <PlusIcon />
                {t('nav_new_organization')}
              </button>
            </li>
          </ul>,
          document.body
        )}
    </div>
  )
}
